import React, { useEffect, useState, useRef } from "react";
import { Book, Users, Newspaper, ArrowRight } from "lucide-react";
import { createClient } from "contentful";

const WritersCorner = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  // Initialize Contentful Client
  const client = createClient({
    space: process.env.REACT_APP_CONTENTFUL_SPACE_ID,
    accessToken: process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN,
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const response = await client.getEntries({
          content_type: "blogPost",
          order: "-sys.createdAt",
          limit: 3,
        });

        const articles = response.items.map((item) => ({
          title: item.fields.title,
          excerpt: item.fields.excerpt,
          slug: item.fields.slug,
          category: item.fields.category,
          readTime: item.fields.readTime || "5 min read",
        }));

        setBlogPosts(articles);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
      }
    };

    fetchBlogPosts();
  }, [client]);

  const icons = [
    <Book className="w-6 h-6" />,
    <Users className="w-6 h-6" />,
    <Newspaper className="w-6 h-6" />
  ];

  return (
    <section ref={sectionRef} className="relative py-24 lg:py-32 bg-cream-100 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-1/4 w-[400px] h-[400px] bg-gold-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-[300px] h-[300px] bg-ink-900/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-gold-500" />
            <span className="text-gold-600 text-sm font-sans tracking-[0.2em] uppercase">
              Writer's Journal
            </span>
            <span className="w-8 h-px bg-gold-500" />
          </div>

          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-ink-900 mb-6">
            Insights & Resources
            <span className="block mt-2 text-gradient-gold">For Authors</span>
          </h2>

          <p className="font-body text-lg text-ink-600 leading-relaxed">
            Discover tips on marketing your books, building your brand, and growing your readership.
          </p>
        </div>

        {/* Blog Posts Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {blogPosts.length > 0 ? (
            blogPosts.map((post, index) => (
              <article
                key={index}
                className={`group relative bg-cream-50 border border-cream-200 transition-all duration-700 hover:border-gold-400/50 hover:shadow-luxury ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                {/* Gold Corner */}
                <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-gold-400/10 to-transparent transform rotate-45 translate-x-12 -translate-y-12 group-hover:from-gold-400/20 transition-all duration-500" />
                </div>

                <div className="p-8">
                  {/* Category & Read Time */}
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-sm font-sans tracking-wide text-gold-600">
                      {post.category}
                    </span>
                    <span className="text-sm text-ink-400 font-sans">
                      {post.readTime}
                    </span>
                  </div>

                  {/* Icon */}
                  <div className="w-12 h-12 flex items-center justify-center bg-gold-100 text-gold-600 rounded-lg mb-6 group-hover:scale-110 transition-transform duration-300">
                    {icons[index]}
                  </div>

                  {/* Content */}
                  <h3 className="font-display text-xl font-semibold text-ink-900 mb-3 group-hover:text-gold-700 transition-colors">
                    <a href={`/blog/${post.slug}`}>{post.title}</a>
                  </h3>

                  <p className="font-body text-ink-600 leading-relaxed mb-6 line-clamp-3">
                    {post.excerpt}
                  </p>

                  {/* Read More Link */}
                  <a
                    href={`/blog/${post.slug}`}
                    className="inline-flex items-center gap-2 text-gold-600 font-sans text-sm tracking-wide group/link"
                  >
                    <span className="w-4 h-px bg-gold-500 group-hover/link:w-6 transition-all duration-300" />
                    Read Article
                    <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform duration-300" />
                  </a>
                </div>

                {/* Bottom Accent Line */}
                <div className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-gold-500 to-gold-600 w-0 group-hover:w-full transition-all duration-500" />
              </article>
            ))
          ) : (
            // Loading State
            [...Array(3)].map((_, index) => (
              <div
                key={index}
                className="bg-cream-50 border border-cream-200 p-8 animate-pulse"
              >
                <div className="flex justify-between mb-6">
                  <div className="w-20 h-4 bg-cream-200 rounded" />
                  <div className="w-16 h-4 bg-cream-200 rounded" />
                </div>
                <div className="w-12 h-12 bg-cream-200 rounded-lg mb-6" />
                <div className="w-3/4 h-6 bg-cream-200 rounded mb-3" />
                <div className="space-y-2">
                  <div className="w-full h-4 bg-cream-200 rounded" />
                  <div className="w-full h-4 bg-cream-200 rounded" />
                  <div className="w-2/3 h-4 bg-cream-200 rounded" />
                </div>
              </div>
            ))
          )}
        </div>

        {/* CTA Button */}
        <div className={`text-center transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <a
            href="/blog"
            className="btn-primary"
          >
            <span className="flex items-center gap-3">
              Visit Our Journal
              <ArrowRight className="w-5 h-5" />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default WritersCorner;
